/**
 * Pannello per la visualizzazione del referto AI di immagini diagnostiche.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { imagingApi, ImagingAnalysis } from '../../api/imaging';
import { DicomViewer } from '../DicomViewer';
import { AlertTriangle, Scan, Search, ClipboardList, RotateCcw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type { ImagingAnalysis };

interface ImagingPanelProps {
  attachmentId: string;
  imageUrl?: string;
  isDicom?: boolean;
  onAnalysisComplete?: (analysis: ImagingAnalysis) => void;
}

function AccuracyGauge({ accuracy }: { accuracy: number | null }) {
  if (accuracy === null) {
    return <div className="text-sm text-muted-foreground">Accuracy: N/A</div>;
  }

  const percentage = Math.round(accuracy * 100);

  let colorClass = 'bg-danger';
  let label = 'Bassa';

  if (accuracy >= 0.8) {
    colorClass = 'bg-success';
    label = 'Alta';
  } else if (accuracy >= 0.6) {
    colorClass = 'bg-warning';
    label = 'Media';
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Accuracy AI:</span>
        <span className={`font-semibold ${colorClass.replace('bg-', 'text-')}`}>
          {percentage}% ({label})
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClass} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function WarningBanner({ accuracy, requiresReview }: { accuracy: number | null, requiresReview: boolean }) {
  if (accuracy !== null && accuracy >= 0.6 && !requiresReview) {
    return null;
  }

  return (
    <div className="bg-warning-bg border border-warning/20 rounded-lg p-3 mb-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="size-icon-ui text-warning shrink-0" />
        <div>
          <p className="font-semibold text-warning">
            Richiede Revisione Veterinario
          </p>
          {accuracy !== null && accuracy < 0.6 && (
            <p className="text-sm text-warning/80 mt-1">
              L&apos;accuratezza dell&apos;analisi è inferiore al 60%.
              Il referto AI deve essere verificato da un veterinario prima di qualsiasi utilizzo.
            </p>
          )}
          {requiresReview && accuracy !== null && accuracy >= 0.6 && (
            <p className="text-sm text-warning/80 mt-1">
              Questo referto richiede comunque verifica veterinaria.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AIDraftBadge() {
  return (
    <div className="bg-danger-bg border border-danger/20 rounded-lg p-2 mb-4">
      <p className="text-sm font-semibold text-danger text-center">
        BOZZA AI — Non inviare al cliente
      </p>
      <p className="text-xs text-danger/70 text-center mt-1">
        Questo referto è uno strumento di supporto decisionale per il veterinario
      </p>
    </div>
  );
}

export function ImagingPanel({ attachmentId, imageUrl, isDicom = false, onAnalysisComplete }: ImagingPanelProps) {
  const queryClient = useQueryClient();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditingCorrection, setIsEditingCorrection] = useState(false);
  const [correctionText, setCorrectionText] = useState('');

  const { data: analysis, isLoading } = useQuery<ImagingAnalysis | null>({
    queryKey: ['imaging-analysis', attachmentId],
    queryFn: () => imagingApi.getAnalysis(attachmentId),
    enabled: !!attachmentId,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => imagingApi.analyze(attachmentId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['imaging-analysis', attachmentId] });
      onAnalysisComplete?.(data);
    },
  });

  const correctionMutation = useMutation({
    mutationFn: (correction: string) => imagingApi.saveCorrection(attachmentId, correction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imaging-analysis', attachmentId] });
      setIsEditingCorrection(false);
      setCorrectionText('');
    },
  });

  const handleSaveCorrection = () => {
    if (correctionText.trim()) {
      correctionMutation.mutate(correctionText.trim());
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-4 bg-muted rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-8">
          <Scan className="size-avatar-md mx-auto mb-3 text-muted-foreground/40" />
          <h3 className="text-base font-semibold text-foreground mb-2">
            Nessuna analisi disponibile
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Questa immagine non è stata ancora analizzata dall&apos;AI.
          </p>
          <Button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            size="sm"
          >
            <Scan className="size-icon-inline" />
            {analyzeMutation.isPending ? 'Analisi in corso...' : 'Analizza Immagine'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="bg-muted/30 px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Scan className="w-4 h-4 text-muted-foreground" />
          Referto AI Immagine Diagnostica
        </h3>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Modello: {analysis.model_used}
        </p>
      </div>

      <div className="p-4 space-y-4">
        <AIDraftBadge />

        <WarningBanner
          accuracy={analysis.accuracy_score}
          requiresReview={analysis.requires_vet_review}
        />

        {imageUrl && (
          <div className="relative">
            {isDicom ? (
              <DicomViewer imageUrl={imageUrl} alt="Immagine DICOM" className="h-64" />
            ) : (
              <>
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="w-full h-48 bg-muted rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                >
                  <img
                    src={imageUrl}
                    alt="Immagine diagnostica"
                    className="w-full h-full object-contain"
                  />
                </button>
                <p className="text-xs text-muted-foreground mt-1 text-center">
                  Clicca per ingrandire
                </p>
              </>
            )}
          </div>
        )}

        <AccuracyGauge accuracy={analysis.accuracy_score} />

        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Search className="size-icon-inline" />
            Findings (Osservazioni)
          </h4>
          <div className="bg-muted/30 rounded-lg p-3">
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">
              {analysis.findings}
            </p>
          </div>
        </div>

        {analysis.diagnosis && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <ClipboardList className="size-icon-inline" />
              Diagnosi Principale
            </h4>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <p className="text-sm font-medium text-primary">
                {analysis.diagnosis}
              </p>
            </div>
          </div>
        )}

        {analysis.differential && analysis.differential.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <RotateCcw className="size-icon-inline" />
              Diagnosi Differenziali
            </h4>
            <ul className="space-y-1">
              {analysis.differential.map((diff, index) => (
                <li
                  key={index}
                  className="text-sm text-foreground/80 bg-muted/30 rounded px-3 py-2"
                >
                  {index + 1}. {diff}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-border pt-4">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Pencil className="size-icon-inline" />
            Correzione Veterinario
          </h4>

          {analysis.vet_correction ? (
            <div className="bg-success-bg border border-success/20 rounded-lg p-3 mb-3">
              <p className="text-sm text-success whitespace-pre-wrap">
                {analysis.vet_correction}
              </p>
            </div>
          ) : null}

          {isEditingCorrection ? (
            <div className="space-y-2">
              <textarea
                value={correctionText}
                onChange={(e) => setCorrectionText(e.target.value)}
                className="textarea h-24"
                placeholder="Inserisci la tua correzione o note aggiuntive..."
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveCorrection}
                  disabled={correctionMutation.isPending || !correctionText.trim()}
                  size="sm"
                >
                  {correctionMutation.isPending ? 'Salvataggio...' : 'Salva Correzione'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditingCorrection(false);
                    setCorrectionText('');
                  }}
                >
                  Annulla
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="link"
              className="p-0 h-auto text-sm text-primary hover:text-primary font-medium"
              onClick={() => setIsEditingCorrection(true)}
            >
              + Aggiungi correzione
            </Button>
          )}
        </div>
      </div>

      {isFullscreen && imageUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <div className="relative max-w-5xl max-h-full">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(false)}
              className="absolute -top-10 right-0 text-white hover:text-white/70"
            >
              ✕
            </Button>
            <img
              src={imageUrl}
              alt="Immagine diagnostica fullscreen"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
